import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { APIReturnType, UserData } from "../types";
import { useLocation, useNavigate } from "react-router-dom";
import { useAPI } from "../hooks/useAPI";

interface AuthContextData {
  user?: UserData;
  isAuthenticated: boolean;
  token?: string;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<APIReturnType>;
  signOut: () => void;
  forgotPassword: (email: string) => Promise<APIReturnType>;
  resetPassword: (
    email: string,
    password: string,
    password_confirmation: string,
    token: string,
  ) => Promise<APIReturnType>;
  signUp: (formData: any) => Promise<APIReturnType>;
  updateProfile: (formData: any, id: string) => Promise<APIReturnType>;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<AuthContextData>({
  user: {},
  isAuthenticated: false,
  loading: true,
  signIn: async () => ({ errors: null, data: null }),
  signOut: () => {},
  forgotPassword: async () => ({ errors: null, data: null }),
  resetPassword: async () => ({ errors: null, data: null }),
  signUp: async () => ({ errors: null, data: null }),
  updateProfile: async () => ({ errors: null, data: null }),
});

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserData>({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true); // <-- Add loading
  const { fetchData, token, setToken } = useAPI();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setLoading(false);
      return;
    }

    validateToken(storedToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) return;

    const publicRoutes = [
      "/signin",
      "/signup",
      "/password/forgot",
      "/password/reset",
    ];

    if (!isAuthenticated && !publicRoutes.includes(location.pathname)) {
      navigate("/signin");
    }

    if (isAuthenticated && publicRoutes.includes(location.pathname)) {
      navigate("/");
    }
  }, [isAuthenticated, loading, location.pathname, navigate]);

  const validateToken = async (storedToken: string) => {
    try {
      const { errors, data } = await fetchData("/user", {}, "GET", storedToken);
      if (errors || !data) {
        updateToken("");
        updateUser({});
        setIsAuthenticated(false);
      } else {
        updateToken(storedToken);
        updateUser(data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error("Token validation failed", error);
      updateToken("");
    } finally {
      setLoading(false);
    }
  };

  const updateToken = (token: string) => {
    setToken(token);
    if (token) localStorage.setItem("token", token);
    else localStorage.removeItem("token");
  };

  const updateUser = (data: UserData) => {
    setUser(data);
    if (data && Object.keys(data).length > 0) {
      localStorage.setItem("userData", JSON.stringify(data));
    } else {
      localStorage.removeItem("userData");
    }
  };

  const signIn = async (email: string, password: string) => {
    const { errors, data } = await fetchData(
      "/login",
      { email, password },
      "POST",
    );

    if (errors || !data) {
      return { errors, data: null };
    }

    updateToken(data.token ?? "");
    updateUser(data.user);
    setIsAuthenticated(true);
    navigate("/");
    return { errors: null, data: null };
  };

  const signOut = () => {
    setIsAuthenticated(false);
    updateUser({});
    updateToken("");
    navigate("/signin");
  };

  const forgotPassword = async (email: string) => {
    return await fetchData("/password/forgot", { email }, "POST");
  };

  const resetPassword = async (
    email: string,
    password: string,
    password_confirmation: string,
    token: string,
  ) => {
    return await fetchData(
      "/password/reset",
      { email, password, password_confirmation, token },
      "POST",
    );
  };

  const signUp = async (formData: any) => {
    const { errors, data } = await fetchData("/signup", formData, "POST");

    if (errors || !data) {
      return { errors, data: null };
    }

    updateToken(data.token);
    updateUser(data.user);
    setIsAuthenticated(true);
    navigate("/");
    return { errors: null, data: null };
  };

  const updateProfile = async (formData: any, id: string) => {
    const { errors, data } = await fetchData(
      "/profile",
      { ...formData, id },
      "POST",
    );
    if (errors) return { errors, data };

    if (data.token) setToken(data.token);
    setUser(data.user);
    navigate("/");
    return { errors: null, data: null };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        token,
        loading,
        signIn,
        signOut,
        forgotPassword,
        resetPassword,
        signUp,
        updateProfile,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
